import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api.js';
import { MAP_TILES } from '../../config/index.js';
import { MENU_ITEMS, routeFor } from '../../layouts/menuData.js';
import './station-locator-availability.scss';

const BLUE = { color: '#005293' };
const peso = (v) => `₱${Number(v).toFixed(2)}`;
const IN_USE = ['Preparing', 'Charging', 'SuspendedEV', 'SuspendedEVSE', 'Finishing'];
const PLUG_LABEL = { CCS2: 'CCS2', Type2: 'Type 2', CHAdeMO: 'CHAdeMO', GBT: 'GB/T' };

const connBadge = (s) => (s === 'Available' ? 'badge-available' : IN_USE.includes(s) ? 'badge-charging' : s === 'Reserved' ? 'badge-reserved' : 'badge-faulted');
const connLabel = (s) => (s === 'Available' ? 'Available' : IN_USE.includes(s) ? 'Occupied' : s === 'Reserved' ? 'Reserved' : 'Offline');
const pinClass = (m) => (m.status !== 'operational' ? 'faulted' : m.availableConnectors > 0 ? 'available' : 'busy');
const pinIcon = (m) => (m.status !== 'operational' ? 'fa-triangle-exclamation' : m.availableConnectors > 0 ? 'fa-bolt' : 'fa-clock');

function siteBadge(s) {
  const avail = s.connectors.some((c) => c.status === 'Available');
  if (s.status !== 'operational') return ['badge-faulted', 'Under Maintenance'];
  return avail ? ['badge-available', 'Plugs Available'] : ['badge-reserved', 'Busy'];
}

const isPhone = () => window.innerWidth < 992;

const go = (slug, navigate, siteId) => {
  const item = MENU_ITEMS.find((m) => m.slug === slug);
  const route = item ? routeFor(item) : '/404';
  navigate(route === '/404' ? route : `${route}?site_id=${siteId || ''}`);
};

export default function StationLocatorPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [plug, setPlug] = useState('ALL');
  const [power, setPower] = useState('ALL');
  const [status, setStatus] = useState('Available');
  const [pageSize, setPageSize] = useState(4);
  const [page, setPage] = useState(1);
  const [stations, setStations] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 4 });
  const [markers, setMarkers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('split');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false); // phones: filters are tucked behind a toggle
  const [locating, setLocating] = useState(false);
  const [geoMsg, setGeoMsg] = useState('');

  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const mapEl = useRef(null);
  const selectRef = useRef(null);
  const hereRef = useRef(null);
  const markersRef = useRef([]);
  const herePos = useRef(null);
  const [here, setHere] = useState(null); // driver's position once shared: list and map then lead with the nearest station
  const pickNearest = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 200);
    return () => clearTimeout(t);
  }, [search]);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    if (debounced) p.set('q', debounced);
    if (plug !== 'ALL') p.set('connectorType', plug);
    if (power !== 'ALL') p.set('power', power);
    if (status !== 'ALL') p.set('status', status);
    if (here) { p.set('lat', here.lat.toFixed(3)); p.set('lng', here.lng.toFixed(3)); } // ~100 m precision is plenty for ordering
    return p;
  }, [debounced, plug, power, status, here]);

  useEffect(() => { setPage(1); }, [debounced, plug, power, status, pageSize]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const p = params();
    p.set('page', String(page));
    p.set('pageSize', String(pageSize));
    api.requestWithMeta(`/public/stations?${p}`)
      .then((r) => {
        if (!live) return;
        setStations(r.data);
        setMeta(r.meta);
        setError('');
        // The first list after the driver shares a position puts the nearest station in focus.
        const takeFirst = pickNearest.current && p.has('lat');
        if (takeFirst) pickNearest.current = false;
        setSelected((cur) => (takeFirst ? r.data[0] : cur) || r.data[0] || null);
      })
      .catch((e) => live && setError(e.message))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [params, page, pageSize]);

  useEffect(() => {
    let live = true;
    api.request(`/public/stations/markers?${params()}`).then((m) => live && setMarkers(m)).catch(() => {});
    return () => { live = false; };
  }, [params]);

  const selectStation = useCallback(async (id) => {
    try {
      const s = await api.request(`/public/stations/${id}`);
      setSelected(s);
      const map = mapRef.current;
      if (map) map.flyTo([s.latitude, s.longitude], 15, { duration: 0.8 });
    } catch (e) {
      setError(e.message);
    }
  }, []);
  selectRef.current = selectStation;

  // Map init
  useEffect(() => {
    const map = L.map(mapEl.current, { zoomControl: true, scrollWheelZoom: true }).setView([14.5503, 121.0505], 12);
    L.tileLayer(MAP_TILES.url, MAP_TILES.options).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    const t = setTimeout(() => map.invalidateSize(), 300);
    return () => { clearTimeout(t); map.remove(); mapRef.current = null; };
  }, []);

  // Markers follow the active filters
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    markers.forEach((m) => {
      const icon = L.divIcon({
        html: `<div class="custom-station-pin"><div class="marker-bubble ${pinClass(m)}"><i class="fa ${pinIcon(m)}"></i> ${String(m.name).split(' ')[0].replace(/[<>&]/g, '')}</div></div>`,
        className: 'leaflet-custom-marker', iconSize: [110, 30], iconAnchor: [55, 15], popupAnchor: [0, -18],
      });
      const mk = L.marker([m.latitude, m.longitude], { icon, title: m.name, alt: m.name }).addTo(layer);
      mk.on('click', () => selectRef.current(m.id));
    });
    markersRef.current = markers;
    fitMap();
     
  }, [markers]);

  // Ask for the driver's position when the page opens: the nearest station then leads the list and the map.
  // A refusal is silent here — the page simply keeps its default order — and the map button can retry.
  useEffect(() => {
    locate(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 200);
    return () => clearTimeout(t);
  }, [view]);

  const activeFilters = Number(plug !== 'ALL') + Number(power !== 'ALL') + Number(status !== 'Available'); // 'Available' is the default

  /**
   * Frames the map. With the driver's position known: the driver and the three nearest stations. Otherwise the
   * whole filtered network on desktop, and on phones the busiest cluster — a country-wide view is unreadable there.
   */
  const fitMap = (all = false) => {
    const map = mapRef.current;
    const list = markersRef.current;
    if (!map || !list.length) return;
    const point = (m) => [Number(m.latitude), Number(m.longitude)];
    let points = list.map(point);
    if (!all && herePos.current) {
      const [lat, lng] = herePos.current;
      // Up to three nearest stations, but never one far beyond the closest: a charger 150 km away
      // must not zoom the map out past the two that are around the corner.
      const away = (m) => Math.hypot(m.latitude - lat, m.longitude - lng);
      const sorted = [...list].sort((x, y) => away(x) - away(y));
      const reach = Math.max(0.25, away(sorted[0]) * 4); // degrees; 0.25 is roughly 25 km
      const nearest = sorted.slice(0, 3).filter((m, i) => i === 0 || away(m) <= reach);
      points = [herePos.current, ...nearest.map(point)];
    } else if (!all && isPhone()) {
      const near = (a) => list.filter((b) => Math.abs(a.latitude - b.latitude) < 0.75 && Math.abs(a.longitude - b.longitude) < 0.75);
      points = list.map(near).reduce((best, group) => (group.length > best.length ? group : best), []).map(point);
    }
    const pad = isPhone() ? [28, 28] : [50, 50];
    map.fitBounds(points, { padding: pad, maxZoom: 13 });
  };

  // "Near me": centre the map on the driver's position (asks the browser for permission the first time).
  const locate = (quiet = false) => {
    if (!navigator.geolocation) { if (!quiet) setGeoMsg('Location is not available on this device.'); return; }
    setLocating(true);
    setGeoMsg('');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setLocating(false);
        const map = mapRef.current;
        if (!map) return;
        if (hereRef.current) hereRef.current.remove();
        hereRef.current = L.circleMarker([coords.latitude, coords.longitude], { radius: 8, color: '#ffffff', weight: 3, fillColor: '#2563eb', fillOpacity: 1 }).addTo(map);
        herePos.current = [coords.latitude, coords.longitude];
        pickNearest.current = true;
        setHere({ lat: coords.latitude, lng: coords.longitude });
        setPage(1);
        fitMap();
      },
      () => { setLocating(false); if (!quiet) setGeoMsg('Could not get your location. Allow location access and try again.'); },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  };

  const totalPages = Math.max(1, Math.ceil(meta.total / pageSize));
  const start = meta.total ? (page - 1) * pageSize + 1 : 0;
  const end = Math.min(page * pageSize, meta.total);
  const t = selected?.tariff;
  const dirQ = selected ? `${selected.latitude},${selected.longitude}` : '';

  return (
    <div className="container-fluid py-4" id="station-locator-page">
      <div className="row mb-3 mb-lg-4 align-items-center">
        <div className="col-12 col-lg-6 mb-2 mb-lg-0 d-none d-lg-block">
          <div className="d-flex align-items-center mb-1">
            <div className="rounded d-none d-md-flex align-items-center justify-content-center me-3" style={{ width: 44, height: 44, background: '#e0f2fe', border: '1px solid #bae6fd' }}>
              <i className="fa fa-charging-station fa-lg" aria-hidden="true" style={BLUE} />
            </div>
            <div>
              <h1 className="h3 fw-bold mb-0" style={{ color: '#0f172a' }}>EV Charging Plaza Locator</h1>
              <p className="text-muted small mb-0 d-none d-md-block">Find EV chargers, check live plug availability, and reserve connectors.</p>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-6 d-flex flex-wrap justify-content-lg-end align-items-center gap-2 chargeos-action-strip">
          <div className="btn-group me-2 mb-2" role="group" aria-label="View switch">
            <button type="button" className={`btn btn-sm btn-outline-primary${view === 'split' ? ' active' : ''}`} id="btn-view-split" onClick={() => setView('split')}>
              <i className="fa fa-columns me-1" aria-hidden="true" /> Split View
            </button>
            <button type="button" className={`btn btn-sm btn-outline-primary${view === 'list' ? ' active' : ''}`} id="btn-view-list" onClick={() => setView('list')}>
              <i className="fa fa-list me-1" aria-hidden="true" /> List Only
            </button>
          </div>
          <button type="button" className="btn btn-sm btn-warning fw-bold mb-2 me-2" id="header-btn-reserve" onClick={() => go('connector-reservation', navigate)}>
            <i className="fa fa-clock" style={{ marginRight: 6 }} aria-hidden="true" />Quick Reserve (15/30m)
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary mb-2" id="header-btn-guest" onClick={() => navigate('/guest-checkout-session')}>
            <i className="fa fa-qrcode" style={{ marginRight: 6 }} aria-hidden="true" />Guest Walk-Up
          </button>
        </div>
      </div>

      <div className={`card p-3 mb-4 shadow-sm border locator-filters${filtersOpen ? ' open' : ''}`}>
        <div className="row align-items-center">
          <div className="col-12 col-md-4 col-lg-3 mb-2 mb-lg-0 d-flex gap-2">
            <div className="input-group input-group-sm flex-grow-1">
              <span className="input-group-text bg-white border-end-0"><i className="fa fa-search text-muted" aria-hidden="true" /></span>
              <input type="text" id="station-search" className="form-control border-start-0" aria-label="Search stations" placeholder="Search stations by name, city or site code" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <button type="button" className={`btn btn-sm btn-outline-primary d-lg-none locator-filter-toggle${activeFilters ? ' has-filters' : ''}`} id="btn-toggle-filters" aria-expanded={filtersOpen} aria-controls="filter-connector" aria-label={`Filters${activeFilters ? ` (${activeFilters} active)` : ''}`} onClick={() => setFiltersOpen((o) => !o)}>
              <i className="fa fa-sliders" aria-hidden="true" />{activeFilters > 0 && <span className="locator-filter-count">{activeFilters}</span>}
            </button>
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-2 mb-lg-0 locator-filter-col">
            <select id="filter-connector" className="form-select form-select-sm" aria-label="Connector type" value={plug} onChange={(e) => setPlug(e.target.value)}>
              <option value="ALL">All Plugs (Any)</option>
              <option value="CCS2">CCS 2 (DC Fast)</option>
              <option value="Type2">Type 2 (AC Normal)</option>
              <option value="CHAdeMO">CHAdeMO</option>
              <option value="GBT">GB/T</option>
            </select>
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-2 mb-lg-0 locator-filter-col">
            <select id="filter-power" className="form-select form-select-sm" aria-label="Power rating" value={power} onChange={(e) => setPower(e.target.value)}>
              <option value="ALL">All Power Rates</option>
              <option value="FAST">Ultra-Fast (&gt; 100 kW)</option>
              <option value="DC">DC Fast (50 - 100 kW)</option>
              <option value="AC">AC Standard (up to 22 kW)</option>
            </select>
          </div>
          <div className="col-6 col-md-2 col-lg-2 mb-2 mb-lg-0 locator-filter-col">
            <select id="filter-status" className="form-select form-select-sm" aria-label="Connector status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="ALL">Any Status</option>
              <option value="Available">Available Now</option>
              <option value="Occupied">In Use</option>
            </select>
          </div>
          <div className="col-6 col-md-12 col-lg-3 text-end locator-count">
            <span className="text-muted small">
              Showing <strong id="station-range-start" className="fw-bold" style={BLUE}>{start}</strong>–<strong id="station-range-end" className="fw-bold" style={BLUE}>{end}</strong> of <strong id="station-count" className="fw-bold" style={BLUE}>{meta.total}</strong> stations
            </span>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <div className="row">
        <div className={`col-12 mb-4 ${view === 'list' ? 'col-lg-12' : 'col-lg-6'}`} id="station-list-col">
          <div id="station-cards-container" className="d-flex flex-column">
            {loading && !stations.length && <div className="text-center py-4"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div></div>}
            {!loading && !stations.length && (
              <div className="alert alert-info bg-white border p-4 text-center"><i className="fa fa-info-circle me-2 text-primary" />No charging stations match your active search filters.</div>
            )}
            {stations.map((s) => {
              const [bc, bt] = siteBadge(s);
              return (
                <div key={s.id} className={`card p-3 station-card mb-3 shadow-sm${selected?.id === s.id ? ' selected' : ''}`} data-id={s.id} data-testid="station-card" role="button" tabIndex={0} onClick={() => selectStation(s.id)} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); selectStation(s.id); } }}>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h5 className="fw-bold mb-1" style={BLUE}>{s.name}</h5>
                      <p className="text-muted small mb-0"><i className="fa fa-map-marker-alt text-danger me-1" />{s.address}, {s.city}{s.distanceKm != null && <span className="station-distance"> &middot; {s.distanceKm < 10 ? s.distanceKm.toFixed(1) : Math.round(s.distanceKm)} km away</span>}</p>
                    </div>
                    <span className={`badge ${bc}`}>{bt}</span>
                  </div>
                  <div className="my-2">
                    {s.connectors.map((c) => (
                      <span key={c.id} className={`badge ${connBadge(c.status)} me-1 mb-1`}>{PLUG_LABEL[c.type] || c.type} ({c.maxPowerKw} kW) - {connLabel(c.status)}</span>
                    ))}
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top station-card-footer">
                    <div className="station-card-rate">
                      <span className="text-muted small d-block">Energy Rate</span>
                      <span className="fw-bold" style={BLUE}>{s.tariff ? `${peso(s.tariff.energyRate)} / kWh` : 'Rate on site'}</span>
                    </div>
                    <div className="d-flex gap-2 station-card-actions">
                      <a className="btn btn-sm btn-outline-primary d-lg-none station-card-directions" target="_blank" rel="noopener noreferrer" aria-label={`Directions to ${s.name}`} href={`https://www.google.com/maps/dir/?api=1&destination=${s.latitude},${s.longitude}`} onClick={(e) => e.stopPropagation()}>
                        <i className="fa fa-diamond-turn-right" aria-hidden="true" />
                      </a>
                      <button type="button" className="btn btn-sm btn-outline-warning me-2" onClick={(e) => { e.stopPropagation(); go('connector-reservation', navigate, s.id); }}>
                        <i className="fa fa-clock" style={{ marginRight: 6 }} /><span className="d-none d-lg-inline">Quick </span>Reserve<span className="d-none d-lg-inline"> (15/30m)</span>
                      </button>
                      <button type="button" className="btn btn-sm btn-primary" onClick={(e) => { e.stopPropagation(); go('live-charging-telemetry', navigate, s.id); }}>
                        <i className="fa fa-bolt" style={{ marginRight: 6 }} />Charge<span className="d-none d-lg-inline"> Now</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="card p-2 mt-2 shadow-sm border bg-white d-flex flex-row justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <span className="text-muted small me-2">Per page:</span>
              <select id="select-page-size" className="form-select form-select-sm" style={{ width: 70 }} aria-label="Per page" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                <option value="4">4</option>
                <option value="8">8</option>
                <option value="12">12</option>
              </select>
            </div>
            <nav aria-label="Plaza list pagination">
              <ul className="pagination pagination-sm mb-0" id="station-pagination">
                {meta.total > pageSize && (
                  <>
                    <li className={`page-item${page === 1 ? ' disabled' : ''}`}><button type="button" className="page-link" onClick={() => setPage(page - 1)}>&laquo;</button></li>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <li key={p} className={`page-item${p === page ? ' active' : ''}`}><button type="button" className="page-link" onClick={() => setPage(p)}>{p}</button></li>
                    ))}
                    <li className={`page-item${page === totalPages ? ' disabled' : ''}`}><button type="button" className="page-link" onClick={() => setPage(page + 1)}>&raquo;</button></li>
                  </>
                )}
              </ul>
            </nav>
          </div>
        </div>

        <div className="col-12 col-lg-6" id="map-col" style={{ display: view === 'list' ? 'none' : undefined }}>
          <div className="card p-0 overflow-hidden sticky-top shadow-sm border" style={{ top: 85, zIndex: 10 }}>
            <div className="card-header bg-white d-flex justify-content-between align-items-center py-2 px-3 border-bottom">
              <span className="small fw-bold" style={BLUE}><i className="fa fa-map-location-dot me-2 text-primary" aria-hidden="true" />Live Philippine Charging Network Map</span>
              <span className="badge badge-available small"><i className="fa fa-circle me-1" style={{ fontSize: 8 }} />Live OpenStreetMap Network</span>
            </div>
            <div className="locator-map-wrap">
              <div id="map-canvas" ref={mapEl} style={{ height: 520, width: '100%', background: '#f8fafc', position: 'relative' }} />
              <button type="button" className="locator-near-me" id="btn-near-me" onClick={() => locate()} disabled={locating} aria-label="Show chargers near me" title="Show chargers near me">
                <i className={`fa ${locating ? 'fa-spinner fa-spin' : 'fa-location-crosshairs'}`} aria-hidden="true" />
              </button>
              <button type="button" className="locator-near-me locator-show-all" id="btn-show-all" onClick={() => fitMap(true)} aria-label="Show the whole network" title="Show the whole network">
                <i className="fa fa-expand" aria-hidden="true" />
              </button>
              {geoMsg && <div className="locator-geo-msg" role="status">{geoMsg}</div>}
            </div>

            <div id="selected-station-panel" className="p-3 border-top bg-white">
              {selected ? (
                <>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h5 id="panel-station-name" className="fw-bold mb-1" style={BLUE}>{selected.name}</h5>
                      <p id="panel-station-addr" className="text-muted small mb-0"><i className="fa fa-location-dot text-danger me-1" />{selected.address}, {selected.city}</p>
                    </div>
                    <span id="panel-station-status" className={`badge ${selected.status === 'operational' ? 'badge-available' : 'badge-faulted'}`}>{selected.status.toUpperCase()}</span>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <span className="badge badge-secondary small me-1" id="panel-hours"><i className="fa fa-clock me-1" /> {selected.operatingHours || 'Hours on site'}</span>
                  </div>
                  <div className="mb-3" id="panel-connectors">
                    {selected.connectors.map((c) => (
                      <span key={c.id} className={`badge ${connBadge(c.status)} me-1 mb-1`} data-testid="panel-connector">{PLUG_LABEL[c.type] || c.type} · {c.maxPowerKw} kW · {connLabel(c.status)}</span>
                    ))}
                  </div>
                  {t && (
                    <div className="small mb-3" id="panel-tariff-detail">
                      <span className="text-muted d-block mb-1">Tariff: {t.name}</span>
                      <span className="badge badge-secondary me-1">Session {peso(t.sessionFee)}</span>
                      <span className="badge badge-secondary me-1">Energy {peso(t.energyRate)}/kWh</span>
                      <span className="badge badge-secondary me-1">Parking {peso(t.parkingRate)}/hr</span>
                      <span className="badge badge-secondary me-1">Idle {peso(t.idleFee)}/min</span>
                      <span className="badge badge-secondary me-1">Reserve 15m {peso(t.reservationFee15)}</span>
                      <span className="badge badge-secondary">Reserve 30m {peso(t.reservationFee30)}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                    <div>
                      <span className="text-muted small d-block">Energy Rate</span>
                      <span id="panel-tariff" className="fw-bold text-nowrap" style={{ ...BLUE, fontSize: '1.1rem' }}>{t ? `${peso(t.energyRate)} / kWh` : 'Rate on site'}</span>
                    </div>
                    <div className="d-flex flex-wrap gap-2 justify-content-end">
                      <a id="panel-btn-directions" className="btn btn-sm btn-outline-primary" target="_blank" rel="noopener noreferrer" href={`https://www.google.com/maps/dir/?api=1&destination=${dirQ}`}>
                        <i className="fa fa-diamond-turn-right" style={{ marginRight: 6 }} aria-hidden="true" />Get Directions
                      </a>
                      <a id="panel-btn-waze" className="btn btn-sm btn-outline-secondary" target="_blank" rel="noopener noreferrer" href={`https://waze.com/ul?ll=${dirQ}&navigate=yes`}>Waze</a>
                      <button type="button" className="btn btn-sm btn-warning fw-bold" id="panel-btn-reserve" onClick={() => go('connector-reservation', navigate, selected.id)}>
                        <i className="fa fa-clock" style={{ marginRight: 6 }} aria-hidden="true" />Quick Reserve (15/30m)
                      </button>
                      <button type="button" className="btn btn-sm btn-primary fw-bold" id="panel-btn-charge" onClick={() => go('live-charging-telemetry', navigate, selected.id)}>
                        <i className="fa fa-bolt" style={{ marginRight: 6 }} aria-hidden="true" />Charge Now
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-muted small mb-0">Select a station to see details.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
